import React, { useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import { getSupabaseClient } from '../lib/supabaseClient'
import { notifyLeadByEmail } from '../lib/notifyLead'

import { marketingColors as colors, marketingBrand, PAPER } from '../lib/marketingBrand'
import { HeroEmboss, MKRule, PaperTextureOverlay, SketchBreak } from '../components/marketing/MarketingChrome'

const IMG_BASE = 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/'

const TEAM_MEMBERS = [
  {
    name: 'Joe Schafbuch',
    title: 'Founder & Visionary',
    image: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Joe.png',
    phone: null,
    email: null,
    bio: "Joe Schafbuch started Hudson My Home™ because he lived the problem firsthand. As a homeowner and entrepreneur who had spent years building businesses across Iowa, he knew better than most how quickly home maintenance could become one more thing competing for a busy family's time and attention. He believed there was a better way, and he built it. Joe's background is in people, not property. Over a career that spans multiple industries, he has developed a reputation for building teams and cultures where trust comes first and results follow. That same philosophy is woven into every part of Hudson My Home™, from the way the company vets its service providers to the way it communicates with clients. What sets Joe apart as a leader is his conviction that a great client experience starts from the inside out. He invests deeply in the people around him, because he knows that a team that feels supported delivers service that actually feels different. At Hudson My Home™, that shows up in the details, and the details are everything. His vision is straightforward: your home should be a source of comfort, not a source of stress. Joe built Hudson My Home™ to make that a reality for the families who trust it with their most valuable asset.",
  },
  {
    name: 'Cassi Luree',
    title: 'Director of Operations',
    image: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Cassi.png',
    phone: '515-216-4447',
    email: 'cassi@hudsonmyhome.com',
    bio: "Cassi has spent her career doing one thing exceptionally well: building environments where people feel genuinely cared for. As Director of Operations at Hudson My Home™, she brings that same instinct to every aspect of the client experience, ensuring that the systems running behind the scenes are as thoughtful and reliable as the service families feel at their door. Her background spans multiple industries, and in each role she found herself naturally becoming the bridge between people, processes, and the bigger picture. Managing large teams across complex organizations taught her that the best outcomes happen when someone takes full ownership of the entire experience, not just their corner of it. That lesson became the foundation for everything she builds at Hudson My Home™. What drives Cassi is the moment a client realizes they can let go. The mental load of homeownership, the endless list of tasks that lives rent-free in the back of your mind, is something she takes seriously. 'When a client tells me they finally feel free to enjoy a weekend away without guilt,' she says, 'that's when I know we've done our job.' At Hudson My Home™, that's not a bonus. It's the whole point.",
  },
  {
    name: 'Mitch Wagoner',
    title: 'Hudson Home Consultant',
    image: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Mitch_Headshot%20(1).png',
    phone: '515-216-4433',
    email: 'mitch@hudsonmyhome.com',
    bio: "Mitch Wagoner has spent the better part of a decade in sales, but what brought him to Hudson My Home™ was something more personal: he knows the stress of homeownership firsthand, and he wanted to be part of the solution. As a Hudson Home Consultant in the Des Moines metro, his role is to introduce homeowners to a different way of thinking about their homes, one built around proactive care and genuine peace of mind. His path here took an unexpected but fitting detour through the healthcare world. After five years working in a chiropractic office, Mitch developed a deep appreciation for the power of proactive maintenance. A regularly adjusted spine minimizes problems before they start. Hudson My Home™ does the same thing for your home. That parallel clicked immediately for him, and it still shapes how he talks about the service today. What sets Mitch apart is a people-first approach and a commitment to doing things the right way, no shortcuts. He was recognized with a company-wide Accountability award in a previous role, and that same standard follows him here. Every conversation he has with a homeowner, whether at an event, over the phone, or through social media, is an opportunity to genuinely help someone protect their largest investment and get their time back.",
  },
  {
    name: 'Cameron Schmidt',
    title: 'Hudson Home Specialist',
    image: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Cameron.png',
    phone: null,
    email: 'cameron@hudsonmyhome.com',
    bio: "Twelve years of working on homes has given Cameron a rare kind of fluency in how houses behave. As a Hudson Home Specialist at Hudson My Home™, he puts that fluency to work every day, leading proactive maintenance, conducting home assessments, and tackling the repairs and projects members bring his way. When a job calls for a specialist trade, he taps Hudson's vendor network and stays close to the work until it's done right. Out of high school, Cameron headed straight into residential construction and stayed for seven years, picking up nearly every trade along the way. He then shifted into apartment maintenance, where five years of progressively bigger challenges led to a role as Maintenance Manager at a luxury complex as well as a Maintenance Supervisor of the Year award for the BH Des Moines properties after running a 200-unit community on his own. By the time he found Hudson, he was ready for something his old roles couldn't quite offer: deeper relationships with clients and the freedom to chase a higher standard of craft.",
  },
  {
    name: 'Marcela Cardenas',
    title: 'Maintenance Coordinator',
    image: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Marcela.png',
    phone: '515-216-4447',
    email: 'marcela@hudsonmyhome.com',
    bio: "Marcela Cardenas knows that when a client reaches out about their home, what they're really asking for is peace of mind. As Maintenance Coordinator at Hudson My Home™, she makes sure they get exactly that, handling every request from intake to resolution with the kind of care and follow-through that keeps homeowners informed, not involved. Marcela came to Hudson My Home™ with a background in home services coordination, where her attention to detail quickly set her apart. She moved from administrative support into maintenance coordination, learning the ins and outs of vendor management, client communication, and the art of keeping every moving part running smoothly. Over time, she was trusted with more and more responsibility, a progression she still counts as one of her proudest professional moments. What sets Marcela apart is the empathy she brings to every interaction. She puts herself in the client's shoes, genuinely, because she believes that seamless service isn't just about logistics. It's about how people feel when the call is over. 'I want clients to forget about maintenance entirely so they can focus on what actually matters in their lives,' she says. That mindset is exactly what Hudson My Home™ is built on.",
  },
]

const VALUES = [
  {
    title: 'Living Life Fully',
    body: 'Life is meant to be enjoyed. Hard working professionals deserve to experience their resources and spend their time doing what they want \u2014 which is not fixing the caulking on the bathroom sink or cleaning out the dryer vents.',
  },
  {
    title: 'First Class Experiences',
    body: 'We deliver first class experiences through services catered specifically to each home, proactive problem solving, and a standard of care that is taken seriously at every level.',
  },
  {
    title: 'Intelligent Expertise',
    body: 'The future of homeownership is geared toward technology and AI, and HMH is equipped to service those advances, making it a valuable resource for the next five to ten years and beyond.',
  },
  {
    title: 'Full of Love and Care',
    body: 'While offering high class experiences and a feeling of intelligence and professionalism, maintaining a sense of warmth and genuine care is incredibly important. Every visit, every conversation, and every interaction reflects that commitment.',
  },
]

const ease = [0.16, 1, 0.3, 1] as const

function FadeIn({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

const AboutPage: React.FC = () => {
  const [contact, setContact] = useState({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [expandedBios, setExpandedBios] = useState<Record<string, boolean>>({})

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setContact(prev => ({ ...prev, [name]: value }))
    setSubmitError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError(null)
    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
    const subject = contact.subject?.trim() || null
    const messageBody = contact.message?.trim() || null
    const combinedMessage = [subject, messageBody].filter(Boolean).join(' — ') || null

    let supabaseInsertOk = false
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase.from('contact_requests').insert({
        full_name: fullName,
        email: contact.email,
        phone: contact.phone?.trim() || null,
        subject,
        message: messageBody,
      })
      if (error) {
        console.warn('[AboutPage] contact_requests insert failed:', error.message)
      } else {
        supabaseInsertOk = true
      }
    } catch (err) {
      console.warn('[AboutPage] contact_requests insert threw:', err)
    }

    try {
      // Always notify (Resend + GHL via Supabase Edge Function) so we never lose a lead,
      // even if the contact_requests insert was blocked by RLS or a network blip.
      await notifyLeadByEmail({
        source: 'contact',
        full_name: fullName,
        email: contact.email,
        phone: contact.phone?.trim() || undefined,
        message: combinedMessage || undefined,
        payload: {
          subject: subject || undefined,
          firstName: contact.firstName || undefined,
          lastName: contact.lastName || undefined,
          page: 'about',
        },
      })
      setSubmitted(true)
      setContact({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' })
    } catch (err) {
      if (supabaseInsertOk) {
        // Lead is already in the database; treat the notification failure as non-blocking.
        setSubmitted(true)
        setContact({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' })
      } else {
        setSubmitError(
          err instanceof Error ? err.message : 'Something went wrong. Please try again or email us at hello@hudsonmyhome.com.',
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
      <MetaTags
        title="About Us | Hudson My Home"
        description="Founded in Iowa by homeowners tired of reactive maintenance. Learn about the team behind Hudson My Home and our mission to give every homeowner a Hudson."
      />
      <Navbar />
      <main>

        {/* ── Hero ── */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ backgroundColor: PAPER }}>
          <div className="absolute inset-0">
            <img
              src={`${IMG_BASE}singlefamilyhome.jpg`}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${PAPER}f0 0%, ${PAPER}e0 38%, ${PAPER}65 70%, transparent 100%)` }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${PAPER}ea 0%, transparent 35%, transparent 72%, ${PAPER}f2 100%)` }} />
          </div>
          <HeroEmboss />
          <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 lg:px-16 xl:px-20 py-28 md:py-36 lg:py-48">
            <FadeIn className="max-w-2xl">
              <div className="mb-8">
                <MKRule wide />
              </div>
              <h1
                className="font-serif font-normal"
                style={{ color: colors.tealDark, fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)', lineHeight: 1.05, letterSpacing: '-0.03em' }}
              >
                Built for Homeowners Who Have{'\n'}Better Things to Do.
              </h1>
              <p className="mt-6 text-lg leading-[1.85] max-w-xl font-sans" style={{ color: colors.drillbitSkies }}>
                Hudson My Home™ is a premium, proactive home management service built for homeowners who value their time, their property, and their peace of mind. Here&apos;s why we built it, and what we believe.
              </p>
              <Link
                to="/quiz"
                className="mt-8 inline-flex items-center gap-2 px-10 py-5 font-medium font-sans text-[15px] tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-70 border"
                style={{ borderColor: colors.plaster, color: colors.drillbitSkies }}
              >
                Discover Your Archetype <ArrowRight size={16} />
              </Link>
            </FadeIn>
          </div>
        </section>

        <SketchBreak />

        {/* ── Founder Story ── */}
        <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
          <PaperTextureOverlay opacity={0.035} />
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <FadeIn>
                <div className="rounded-sm overflow-hidden shadow-[0_24px_60px_-12px_rgba(13,49,53,0.18)] ring-1 ring-black/[0.06] aspect-[4/3]">
                  <img
                    src={`${IMG_BASE}babywithhandsovermom.jpg`}
                    alt="Family moments"
                    className="h-full w-full object-cover"
                  />
                </div>
              </FadeIn>
              <FadeIn delay={0.15}>
                <h2
                  className="font-serif font-normal"
                  style={{ color: colors.nearBlack, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
                >
                  There Had to Be a Better Way.{'\n'}So We Built It.
                </h2>
                <div className="mt-8 space-y-5 text-base leading-[1.85] font-sans" style={{ color: colors.drillbitSkies }}>
                  <p>
                    It started on a Saturday morning Joe will never get back. His daughter had a soccer game. His son wanted to ride bikes. Instead, Joe spent the morning on hold with an HVAC company, then drove across town to pick up a furnace filter nobody had in stock. By the time he got home, the game was over. The moment was gone.
                  </p>
                  <p>
                    As a father and family man, Joe valued time freedom above all else. He knew that the moments spent with loved ones — coaching a little league game, sitting down for dinner, or enjoying a quiet weekend — were priceless. But too often, home maintenance got in the way. Instead of spending his free time making memories, he found himself fixing leaks, scheduling repairs, and handling last-minute home issues.
                  </p>
                  <p>
                    That&apos;s when Hudson My Home™ was born. Joe envisioned a service that eliminated the stress of home maintenance entirely — not just for entrepreneurs like himself, but for every busy homeowner who wanted to enjoy their home without the burden of upkeep. Because at the end of the day, a home isn&apos;t just a place to maintain. It&apos;s a place to live, love, and create lasting memories.
                  </p>
                  <p>
                    When Cassi Luree joined as Director of Operations, the vision became a system. Cassi brought years of experience building environments where people feel genuinely cared for, and she turned that instinct into the operational backbone of Hudson My Home™. From client onboarding to vendor coordination to proactive scheduling, she ensures that the service families experience at their door is matched by the systems running behind it. Her philosophy is simple: when a client tells us they finally feel free to enjoy a weekend without guilt, that&apos;s when we&apos;ve done our job.
                  </p>
                </div>
                <Link
                  to="/services"
                  className="mt-8 inline-flex items-center gap-2 px-10 py-5 font-medium font-sans text-[15px] tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110"
                  style={{ backgroundColor: colors.copper }}
                >
                  Discover Home Membership
                </Link>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ── Values ── */}
        <section className="relative overflow-hidden" style={{ backgroundColor: colors.tealDark }}>
          <PaperTextureOverlay opacity={0.025} />
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <FadeIn>
                <div className="rounded-sm overflow-hidden shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] ring-1 ring-white/[0.06] aspect-[4/5]">
                  <img
                    src={`${IMG_BASE}familyhugging.png`}
                    alt="Family embracing"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </FadeIn>
              <div>
                <FadeIn>
                  <h2
                    className="font-serif font-normal text-white mb-10"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
                  >
                    What We Believe Shows Up In{' '}
                    <span className="italic" style={{ color: colors.copper }}>Everything We Do.</span>
                  </h2>
                </FadeIn>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                  {VALUES.map((v, i) => (
                    <FadeIn key={v.title} delay={0.1 + i * 0.08}>
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="block w-6 border-t border-dashed" style={{ borderColor: colors.copper }} />
                          <span className="block w-3 border-t border-dashed" style={{ borderColor: colors.copper }} />
                        </div>
                        <h3 className="font-serif text-lg mb-2 text-white">{v.title}</h3>
                        <p className="text-sm leading-relaxed font-sans text-white/70">{v.body}</p>
                      </div>
                    </FadeIn>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── What We're Not ── */}
        <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
          <PaperTextureOverlay opacity={0.035} />
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <FadeIn>
                <h2
                  className="font-serif font-normal"
                  style={{ color: colors.nearBlack, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
                >
                  Not a Handyman Service.<br />
                  Not a Repair Company.<br />
                  <span className="italic" style={{ color: colors.copper }}>Something Entirely Different.</span>
                </h2>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div className="space-y-6 text-base leading-[1.85] font-sans" style={{ color: colors.drillbitSkies }}>
                  <p>
                    Unlike traditional home services, Hudson My Home™ offers a white-glove, concierge-style experience &mdash; ensuring that every aspect of a home&apos;s upkeep is handled with expert precision and efficiency.
                  </p>
                  <p>
                    By leveraging AI-driven predictive maintenance, a membership model, and a luxury service experience, HMH helps clients eliminate the burdens of homeownership so they can focus on living life fully. We don&apos;t wait for things to break. We ensure they never do.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ── Team ── */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={`${IMG_BASE}Meet%20the%20Team%20background.jpg`}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundColor: `${PAPER}e8` }} />
          </div>
          <PaperTextureOverlay opacity={0.03} />
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
            <FadeIn className="text-center mb-6">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>Our Team</p>
              <div className="flex justify-center mb-6"><MKRule /></div>
              <h2
                className="font-serif font-normal"
                style={{ color: colors.tealDark, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
              >
                Meet The People Behind Your Peace of Mind.
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-16">
              {TEAM_MEMBERS.map((member, i) => {
                const isExpanded = expandedBios[member.name] ?? false
                return (
                  <FadeIn key={member.name} delay={i * 0.1}>
                    <div className="flex flex-col items-center text-center">
                      <div className="w-40 h-40 rounded-sm overflow-hidden shadow-[0_24px_60px_-12px_rgba(13,49,53,0.18)] ring-1 ring-black/[0.06] mb-6" style={{ backgroundColor: colors.plaster }}>
                        <img
                          src={member.image}
                          alt={member.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <h3 className="font-serif text-lg" style={{ color: colors.tealDark }}>
                        {member.name}
                      </h3>
                      <p className="font-sans text-sm mt-1" style={{ color: colors.copper }}>
                        {member.title}
                      </p>
                      {(member.phone || member.email) && (
                        <div className="mt-2 space-y-0.5 text-xs font-sans" style={{ color: colors.drillbitSkies }}>
                          {member.phone && (
                            <p><a href={`tel:${member.phone.replace(/-/g, '')}`} className="hover:underline">{member.phone}</a></p>
                          )}
                          {member.email && (
                            <p><a href={`mailto:${member.email}`} className="hover:underline">{member.email}</a></p>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpandedBios(prev => ({ ...prev, [member.name]: !isExpanded }))}
                        className="mt-4 inline-flex items-center gap-1 font-sans text-sm font-medium transition-colors hover:opacity-80"
                        style={{ color: colors.teal }}
                      >
                        {isExpanded ? 'Hide Bio' : 'View Full Bio'}
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.35, ease }}
                          className="mt-4 text-left"
                        >
                          <p className="text-sm leading-relaxed font-sans" style={{ color: colors.drillbitSkies }}>
                            {member.bio}
                          </p>
                        </motion.div>
                      )}
                    </div>
                  </FadeIn>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── What Is a Hudson? ── */}
        <section className="relative py-24 md:py-32 lg:py-40 overflow-hidden" style={{ backgroundColor: PAPER }}>
          <PaperTextureOverlay opacity={0.035} />
          <div className="relative z-10 mx-auto max-w-3xl px-6 md:px-10 lg:px-16 text-center">
            <FadeIn>
              <h2
                className="font-serif font-normal"
                style={{ color: colors.nearBlack, fontSize: 'clamp(2rem, 4.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
              >
                What Is a Hudson?
              </h2>
            </FadeIn>
            <FadeIn delay={0.08}>
              <p className="mt-8 font-sans text-base sm:text-lg leading-[1.85] max-w-2xl mx-auto" style={{ color: colors.drillbitSkies }}>
                A Hudson is the person every homeowner wishes they had. Someone who knows your
                home&apos;s systems, anticipates what it needs, coordinates every vendor, and
                handles everything before you have to think about it. Someone who treats your
                home the way you would if you had unlimited time and expertise.
              </p>
              <p
                className="mt-6 font-serif italic text-lg sm:text-xl"
                style={{ color: colors.copper }}
              >
                That&apos;s what a Hudson is. And every homeowner deserves one.
              </p>
            </FadeIn>
          </div>
        </section>

        {/* ── Contact Form ── */}
        <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }}>
          <PaperTextureOverlay opacity={0.035} />
          <div className="relative z-10 mx-auto max-w-3xl px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
            <FadeIn className="text-center mb-6">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>Get In Touch</p>
              <div className="flex justify-center mb-6"><MKRule /></div>
              <h2
                className="font-serif font-normal"
                style={{ color: colors.tealDark, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
              >
                Contact Us
              </h2>
            </FadeIn>
            <FadeIn delay={0.05} className="text-center mb-12">
              <h3
                className="font-serif font-normal mb-4"
                style={{ color: colors.teal, fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.15 }}
              >
                Ready to See What Handled Feels Like?
              </h3>
              <a
                href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-10 py-5 text-white font-medium font-sans text-[15px] tracking-[0.06em] uppercase transition-all duration-500 hover:brightness-110"
                style={{ backgroundColor: colors.copper }}
              >
                Book Your Consultation <ArrowRight size={16} />
              </a>
              <p className="mt-3 text-sm font-sans" style={{ color: colors.drillbitSkies, opacity: 0.75 }}>Or send us a message below.</p>
            </FadeIn>
            <FadeIn delay={0.1}>
              {submitted ? (
                <div className="text-center py-12">
                  <p className="text-lg font-medium" style={{ color: colors.teal }}>
                    Thanks for reaching out! We&apos;ll be in touch soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <input
                      name="firstName"
                      value={contact.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      required
                      className="h-12 px-4 rounded-sm text-sm font-sans focus:outline-none focus:ring-2 transition"
                      style={{ border: `1px solid ${colors.plaster}`, backgroundColor: marketingBrand.white, color: colors.tealDark }}
                    />
                    <input
                      name="lastName"
                      value={contact.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className="h-12 px-4 rounded-sm text-sm font-sans focus:outline-none focus:ring-2 transition"
                      style={{ border: `1px solid ${colors.plaster}`, backgroundColor: marketingBrand.white, color: colors.tealDark }}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <input
                      name="email"
                      type="email"
                      value={contact.email}
                      onChange={handleChange}
                      placeholder="Email"
                      required
                      className="h-12 px-4 rounded-sm text-sm font-sans focus:outline-none focus:ring-2 transition"
                      style={{ border: `1px solid ${colors.plaster}`, backgroundColor: marketingBrand.white, color: colors.tealDark }}
                    />
                    <input
                      name="phone"
                      value={contact.phone}
                      onChange={handleChange}
                      placeholder="Phone"
                      className="h-12 px-4 rounded-sm text-sm font-sans focus:outline-none focus:ring-2 transition"
                      style={{ border: `1px solid ${colors.plaster}`, backgroundColor: marketingBrand.white, color: colors.tealDark }}
                    />
                  </div>
                  <input
                    name="subject"
                    value={contact.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                    className="w-full h-12 px-4 rounded-sm text-sm font-sans focus:outline-none focus:ring-2 transition"
                    style={{ border: `1px solid ${colors.plaster}`, backgroundColor: marketingBrand.white, color: colors.tealDark }}
                  />
                  <textarea
                    name="message"
                    value={contact.message}
                    onChange={handleChange}
                    placeholder="Message"
                    rows={5}
                    className="w-full px-4 py-3 rounded-sm text-sm font-sans focus:outline-none focus:ring-2 transition resize-none"
                    style={{ border: `1px solid ${colors.plaster}`, backgroundColor: marketingBrand.white, color: colors.tealDark }}
                  />
                  {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
                  <div className="text-center pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-10 py-5 text-white font-medium font-sans text-[15px] tracking-[0.06em] uppercase transition-all duration-500 hover:brightness-110 disabled:opacity-60"
                      style={{ backgroundColor: colors.teal }}
                    >
                      {submitting ? 'Sending\u2026' : 'Submit'}
                    </button>
                  </div>
                </form>
              )}
            </FadeIn>
          </div>
        </section>

        {/* ── Footer CTA ── */}
        <section
          className="relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${colors.tealDark} 0%, ${colors.teal} 100%)` }}
        >
          <PaperTextureOverlay opacity={0.02} />
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40 text-center">
            <FadeIn>
              <p
                className="font-serif font-normal text-white"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
              >
                We Maintain Your Home{'\n'}So You Don&apos;t Have To.
              </p>
              <Link
                to="/services"
                className="mt-10 inline-flex items-center gap-2 px-10 py-5 font-medium font-sans text-[15px] tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110"
                style={{ backgroundColor: colors.copper }}
              >
                Discover Home Membership <ArrowRight size={16} />
              </Link>
            </FadeIn>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  )
}

export default AboutPage
